import { FC, ReactNode, useLayoutEffect, useRef } from 'react';

export const ANIMATED_HEIGHT_DURATION_MS = 300;

type AnimatedHeightProps = {
  /**
   * Names the content shown (a wizard step). When it changes, the container eases from the old
   * content's height to the new one's instead of jumping.
   */
  animationKey: string;
  children: ReactNode;
  className?: string;
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const release = (container: HTMLElement) => {
  container.style.removeProperty('height');
  container.style.removeProperty('overflow');
  container.style.removeProperty('transition');
};

/**
 * Eases a container between the heights of whole content swaps. Between swaps the height stays
 * `auto` and nothing is clipped, so content that animates its own size inside (a reveal) drives the
 * layout directly.
 */
export const AnimatedHeight: FC<AnimatedHeightProps> = ({ animationKey, children, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The content's height as last laid out. A swap's layout effect runs before the observer
  // reports the new size, so it still holds the outgoing content's height there.
  const settledHeightRef = useRef(0);
  const animationKeyRef = useRef(animationKey);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const measure = () => {
      settledHeightRef.current = content.getBoundingClientRect().height;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (animationKeyRef.current === animationKey || !container || !content) return;
    animationKeyRef.current = animationKey;

    // A swap that lands mid-animation continues from wherever the container is.
    const from = container.style.height
      ? container.getBoundingClientRect().height
      : settledHeightRef.current;
    const to = content.getBoundingClientRect().height;

    if (prefersReducedMotion() || Math.abs(from - to) < 1) {
      release(container);
      return;
    }

    container.style.transition = 'none';
    container.style.overflow = 'hidden';
    container.style.height = `${from}px`;
    // Lay out the start height before transitioning, or the browser starts from `auto`.
    void container.offsetHeight;
    container.style.transition = `height ${ANIMATED_HEIGHT_DURATION_MS}ms ease-in-out`;
    container.style.height = `${to}px`;

    const timeout = window.setTimeout(() => release(container), ANIMATED_HEIGHT_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [animationKey]);

  return (
    <div className={className} ref={containerRef}>
      <div ref={contentRef}>{children}</div>
    </div>
  );
};
